import { UI, HStack, Stack, VStack, StackTypes, Skeleton } from '@genial/components';
import { BoxBorder, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { useMasterStateActions } from '../../store/master/actions';
import {
  masterStateAdvisorListLoaded,
  masterStateSelectedAdvisorSelector,
  masterStateSelector,
} from '../../store/master/selector';
import { getUrlParam } from '../../utils/getUrlParam';

import { MasterProfile } from './MasterProfile';

export interface MasterSelectionProps {
  containerStyle?: StackTypes.Props;
  selectContainerStyle?: StackTypes.Props;
  hideMasterProfile?: boolean;
  showSelectedAdvisor?: boolean;
}

export const MasterSelection: React.FC<MasterSelectionProps> = ({
  containerStyle,
  selectContainerStyle,
  hideMasterProfile = false,
  showSelectedAdvisor = false,
}) => {
  const name = getUrlParam('name', 'Master Parceiro');
  const isMaster = getUrlParam('isMaster', 'false') === 'true';

  const { data, loading } = useRecoilQuery(masterStateSelector({ isMaster }));
  const advisorListLoaded = useRecoilValue(masterStateAdvisorListLoaded);
  const selectedAdvisor = useRecoilValue(masterStateSelectedAdvisorSelector);

  const { setMasterState, setNewSelectedAdvisor } = useMasterStateActions();

  const handleChangeMaster = (value: string) => {
    const valueAsInt = parseInt(value, 10);
    const advisorToBeSet = data?.advisorList.find((advisor) => advisor.id === valueAsInt);

    if (advisorToBeSet) {
      setNewSelectedAdvisor(advisorToBeSet.id, advisorToBeSet.name);
    }
  };

  const generateOptions = () => {
    if (!data || !data.advisorList) {
      return [];
    }
    return data.advisorList.map((advisor) => ({
      label: advisor.name || '',
      value: `${advisor.id}`,
    }));
  };

  const renderSelection = () => {
    if (showSelectedAdvisor && selectedAdvisor && data?.advisorList) {
      const advisorSelected = data.advisorList.find((advisor) => advisor.id === selectedAdvisor);
      return (
        <Stack
          bgColor={Color['neutral.white']}
          borderRadius={BoxBorder.thick}
          p={Spacing['inset.nano']}>
          <UI.Text variant="semibold">{advisorSelected?.name}</UI.Text>
        </Stack>
      );
    }
    if (loading) {
      return <Skeleton height={Spacing['stack.xs']} width="full" borderRadius={BoxBorder.thick} />;
    }
    return (
      <Stack bgColor={Color['neutral.white']} borderRadius={BoxBorder.thick}>
        <UI.Select
          placeholder={name}
          onValueChange={handleChangeMaster}
          selectedValue={`${selectedAdvisor}`}
          options={generateOptions()}
          maxOptionPerRender={8}
          maxOptionsContentHeight="80vh"
          optionsDesktopDirection="left"
        />
      </Stack>
    );
  };

  useEffect(() => {
    if (!data || advisorListLoaded) {
      return;
    }
    setMasterState(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!isMaster) {
    return <></>;
  }

  if (showSelectedAdvisor && !selectedAdvisor) {
    return <></>;
  }

  return (
    <HStack space={Spacing['inline.xxxs']} overflow="hidden" {...containerStyle}>
      {!hideMasterProfile && <MasterProfile />}
      <VStack
        justifyContent="space-between"
        w="168px"
        maxW={Utils.Responsive.All({ desktop: '350px', tablet: '250px', phone: 'full' })}
        {...selectContainerStyle}>
        <UI.Text color="neutral.medium" fontSize={Typography['fontsize.xs']}>
          Visualizando dados de:
        </UI.Text>
        {renderSelection()}
      </VStack>
    </HStack>
  );
};
